import { trpc } from './trpc'
import { inferRouterOutputs, inferRouterInputs } from '@trpc/server'
import { AppRouter } from '@tagadapay/trpc-types'
import { useToast } from '@/components/ui/use-toast'

type OutputRouter = inferRouterOutputs<AppRouter>
type InputRouter = inferRouterInputs<AppRouter>

// Output types
export type TaxRateDetails = OutputRouter['taxRates']['getById']['taxRate']
export type TaxRatesListOutput = OutputRouter['taxRates']['list']
export type TaxRateItem = TaxRatesListOutput['items'][number]
export type CreateTaxRateOutput = OutputRouter['taxRates']['create']
export type UpdateTaxRateOutput = OutputRouter['taxRates']['update']
export type DeleteTaxRatesOutput = OutputRouter['taxRates']['delete']

// Input types
export type GetTaxRateInput = InputRouter['taxRates']['getById']
export type ListTaxRatesInput = InputRouter['taxRates']['list']
export type CreateTaxRateInput = InputRouter['taxRates']['create']
export type UpdateTaxRateInput = InputRouter['taxRates']['update']
export type DeleteTaxRatesInput = InputRouter['taxRates']['delete']

// Query hooks
export const useTaxRate = (id: string, enabled = true) => {
	return trpc.taxRates.getById.useQuery(
		{ id },
		{
			enabled: enabled && !!id,
			retry: 2,
			staleTime: 30000,
		},
	)
}

export const useTaxRatesList = (params: ListTaxRatesInput, options?: {
	enabled?: boolean;
	refetchOnMount?: boolean | 'always';
	refetchOnWindowFocus?: boolean;
}) => {
	const { enabled = true, ...otherOptions } = options || {};
	return trpc.taxRates.list.useQuery(params, {
		enabled,
		...otherOptions,
	})
}

// Mutation hooks
export const useCreateTaxRate = () => {
	const { toast } = useToast()
	const utils = trpc.useUtils()

	return trpc.taxRates.create.useMutation({
		onSuccess: () => {
			utils.taxRates.list.invalidate()
			toast({
				title: 'Success',
				description: 'Tax rate created successfully',
			})
		},
		onError: (error) => {
			toast({
				title: 'Error',
				description: error.message || 'Failed to create tax rate',
				variant: 'destructive',
			})
		},
	})
}

export const useUpdateTaxRate = () => {
	const { toast } = useToast()
	const utils = trpc.useUtils()

	return trpc.taxRates.update.useMutation({
		onSuccess: () => {
			utils.taxRates.list.invalidate()
			utils.taxRates.getById.invalidate()
			toast({
				title: 'Success',
				description: 'Tax rate updated successfully',
			})
		},
		onError: (error) => {
			toast({
				title: 'Error',
				description: error.message || 'Failed to update tax rate',
				variant: 'destructive',
			})
		},
	})
}

export const useDeleteTaxRates = () => {
	const { toast } = useToast()
	const utils = trpc.useUtils()

	return trpc.taxRates.delete.useMutation({
		onSuccess: () => {
			utils.taxRates.list.invalidate()
			toast({
				title: 'Success',
				description: 'Tax rate(s) deleted successfully',
			})
		},
		onError: (error) => {
			toast({
				title: 'Error',
				description: error.message || 'Failed to delete tax rate(s)',
				variant: 'destructive',
			})
		},
	})
}

